import { useEffect, useRef, useState } from "react";
import { getSubmission, listDirectUpdateHistory, newIdempotencyKey, searchDirectEmployees, submitDirectPhoto } from "./workflow-api";
import { directUpdateStatusCopy, isDirectUpdateTerminal } from './direct-update-ui';
import "./direct-updater.css";

const DIRECT_REASONS = [
  ["new_joiner", "New joiner / initial profile photo"],
  ["correction", "Correction to an existing profile photo"],
  ["executive_request", "Authorized executive or management request"],
  ["support", "Support or recovery action"],
  ["other", "Other reason"],
];

const reasonLabel = (code) => DIRECT_REASONS.find(([value]) => value === code)?.[1] || code;

function errorMessage(error) {
  return error instanceof Error ? error.message : String(error);
}

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

export default function DirectUpdaterCard({ photoReady, renderPhoto, originalPhoto, getAccessToken, confirmShoulderCrop = async () => true }) {
  const keyRef = useRef(newIdempotencyKey());
  const tokenProvider = useRef(getAccessToken);
  tokenProvider.current = getAccessToken;
  const [targetEmail, setTargetEmail] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [comments, setComments] = useState("");
  const [employee, setEmployee] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tracking, setTracking] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(true);
  const [historyError, setHistoryError] = useState("");

  const ready = photoReady
    && employee?.email === targetEmail.trim().toLowerCase()
    && reasonCode
    && (reasonCode !== "other" || comments.trim());

  function resetKey() {
    keyRef.current = newIdempotencyKey();
  }

  async function loadHistory() {
    setHistoryBusy(true);
    setHistoryError("");
    try {
      const result = await listDirectUpdateHistory(() => tokenProvider.current());
      setHistory(result.submissions || []);
    } catch (loadError) {
      setHistoryError(errorMessage(loadError));
    } finally {
      setHistoryBusy(false);
    }
  }

  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    const query = targetEmail.trim();
    if (employee?.email === query.toLowerCase() || query.length < 2) {
      setSuggestions([]);
      setSearchBusy(false);
      setSearchError("");
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearchBusy(true);
      setSearchError("");
      try {
        const result = await searchDirectEmployees(query, () => tokenProvider.current());
        if (!cancelled) setSuggestions(result.employees || []);
      } catch (lookupFailure) {
        if (!cancelled) {
          setSuggestions([]);
          setSearchError(errorMessage(lookupFailure));
        }
      } finally {
        if (!cancelled) setSearchBusy(false);
      }
    }, 300);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [targetEmail, employee?.email]);

  useEffect(() => {
    if (!tracking?.id || isDirectUpdateTerminal(tracking.status)) return undefined;
    let cancelled = false;
    let timer;
    let attempts = 0;
    const poll = async () => {
      try {
        const result = await getSubmission(tracking.id, () => tokenProvider.current());
        if (cancelled) return;
        const next = { ...tracking, status: result.submission.status, waiting: false };
        setTracking(next);
        setHistory((items) => items.map((item) => item.id === next.id ? { ...item, ...result.submission } : item));
        if (!isDirectUpdateTerminal(next.status) && attempts++ < 39) timer = window.setTimeout(poll, 2500);
        else if (!isDirectUpdateTerminal(next.status)) setTracking({ ...next, waiting: true });
      } catch (pollError) {
        if (!cancelled) setTracking(current => current ? { ...current, pollError: errorMessage(pollError) } : current);
      }
    };
    timer = window.setTimeout(poll, 1200);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [tracking?.id]);

  async function queueDirectUpdate() {
    if (!ready) return;
    const reason = reasonLabel(reasonCode);
    const confirmed = window.confirm(
      `Update the Microsoft 365 profile photo for ${employee.name} (${employee.email})?\n\nReason: ${reason}\n\nThis bypasses HC and CCD approval and will be recorded in the audit trail.`,
    );
    if (!confirmed) return;
    if (!(await confirmShoulderCrop())) return;

    setBusy(true);
    setError("");
    try {
      await getAccessToken();
      const blob = await renderPhoto();
      const result = await submitDirectPhoto(
        blob,
        targetEmail.trim(),
        reasonCode,
        comments.trim(),
        getAccessToken,
        keyRef.current,
        originalPhoto,
      );
      setTracking({ id: result.submission.id, status: result.submission.status, email: targetEmail.trim().toLowerCase(), waiting: false, pollError: '' });
      setHistory((items) => [result.submission, ...items.filter((item) => item.id !== result.submission.id)].slice(0, 25));
      setTargetEmail("");
      setEmployee(null);
      setSuggestions([]);
      setReasonCode("");
      setComments("");
      resetKey();
    } catch (submitError) {
      setError(errorMessage(submitError));
    } finally {
      setBusy(false);
    }
  }

  return <div className="direct-update-card">
    <div>
      <h3>Direct updater</h3>
      <p>Administrative exception only. Update another employee’s Microsoft 365 photo without HC/CCD approval; every action requires a reason and is audited.</p>
    </div>

    <label htmlFor="direct-email">Find employee</label>
    <div className="direct-employee-combobox">
      <input
        id="direct-email"
        type="search"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={suggestions.length > 0}
        aria-controls="direct-employee-suggestions"
        autoComplete="off"
        disabled={busy || Boolean(tracking && !isDirectUpdateTerminal(tracking.status))}
        value={targetEmail}
        onChange={(event) => { setTargetEmail(event.target.value); setEmployee(null); setSearchError(""); resetKey(); }}
        placeholder="Start typing a name or email"
      />
      {searchBusy && <small className="direct-search-state">Searching employees…</small>}
      {suggestions.length > 0 && <div id="direct-employee-suggestions" className="direct-employee-suggestions" role="listbox">
        {suggestions.map((suggestion) => <button key={suggestion.email} type="button" role="option" aria-selected="false" onClick={() => { setEmployee(suggestion); setTargetEmail(suggestion.email); setSuggestions([]); resetKey(); }}>
          <strong>{suggestion.name}</strong><small>{suggestion.email}</small><small>{suggestion.companyName || "Company not recorded"}</small>
        </button>)}
      </div>}
    </div>
    {searchError && <div className="workflow-error" role="alert">{searchError}</div>}
    {employee && <div className="direct-employee-result" role="status">
      <strong>{employee.name}</strong>
      <small>{employee.email}</small>
      <small>{employee.companyName || 'Company not recorded'}</small>
    </div>}

    <label htmlFor="direct-reason">Reason</label>
    <select
      id="direct-reason"
      disabled={busy || Boolean(tracking && !isDirectUpdateTerminal(tracking.status))}
      value={reasonCode}
      onChange={(event) => { setReasonCode(event.target.value); resetKey(); }}
    >
      <option value="">Select a reason</option>
      {DIRECT_REASONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
    </select>

    <label htmlFor="direct-comments">Audit comments {reasonCode === "other" ? "(required)" : "(optional)"}</label>
    <textarea
      id="direct-comments"
      maxLength="1000"
      disabled={busy || Boolean(tracking && !isDirectUpdateTerminal(tracking.status))}
      value={comments}
      onChange={(event) => { setComments(event.target.value); resetKey(); }}
      placeholder={reasonCode === "other" ? "Explain why the direct update is required." : "Add useful audit detail if needed."}
    />

    <div className="direct-update-warning">
      <strong>Approval bypass</strong>
      <small>Only authorized Direct Updater accounts can use this action. The employee, reason, operator and request reference are retained in the workflow audit trail.</small>
    </div>

    <button disabled={busy || !ready || Boolean(tracking && !isDirectUpdateTerminal(tracking.status))} onClick={queueDirectUpdate}>{busy ? "Preparing update…" : "Update Microsoft 365 photo"}</button>
    {!photoReady && <small>Prepare a photo in the editor first.</small>}
    {error && <div className="workflow-error" role="alert">{error}</div>}
    {tracking && <div className={`direct-update-status ${tracking.status === 'Completed' ? 'is-completed' : ''} ${['Update Failed', 'Requires Support'].includes(tracking.status) ? 'is-attention' : ''}`} role="status">
      <strong>{directUpdateStatusCopy(tracking.status).title}</strong>
      <small>{directUpdateStatusCopy(tracking.status).detail}</small>
      <small>Employee: {tracking.email}</small>
      {tracking.waiting && <small>The update is still in progress. You can safely return later; the request reference is {tracking.id}.</small>}
      {tracking.pollError && <small className="direct-update-status-error">Status refresh paused: {tracking.pollError}</small>}
      <small>Reference: {tracking.id}</small>
    </div>}

    <section className="direct-update-history" aria-label="Direct update history">
      <div className="direct-history-heading"><div><h4>Recent direct updates</h4><small>Your 25 most recent Direct Updater requests.</small></div><button type="button" onClick={loadHistory} disabled={historyBusy}>{historyBusy ? "Loading…" : "Refresh"}</button></div>
      {historyError && <div className="workflow-error" role="alert">{historyError}</div>}
      {!historyBusy && !historyError && history.length === 0 && <p>No direct update history yet.</p>}
      <div className="direct-history-list">
        {history.map((item) => <article key={item.id}>
          <div><strong>{item.employeeName}</strong><small>{item.employeeEmail}</small></div>
          <span className={`direct-history-status ${item.status === "Completed" ? "is-completed" : ""} ${["Update Failed", "Requires Support"].includes(item.status) ? "is-attention" : ""}`}>{item.status}</span>
          <small>{formatDate(item.submittedDate)}</small>
          <small className="direct-history-reference">Reference: {item.id}</small>
        </article>)}
      </div>
    </section>
  </div>;
}
